import i18n from '../i18n';

const LOCALE_MAP: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL'
};

export function resolveLocale(): string {
  return LOCALE_MAP[i18n.language] ?? LOCALE_MAP[i18n.language?.split('-')[0]] ?? 'de-DE';
}

/// Backend timestamps with a time component (e.g. BackgroundTask.CreatedAt/StartedAt) are UTC
/// instants but often arrive without a timezone designator - EF Core's Sqlite provider loses
/// DateTimeKind on read-back, so System.Text.Json serializes them as bare "yyyy-MM-ddTHH:mm:ss"
/// instead of appending "Z". Without correcting for that, `new Date(...)` parses them as local
/// time instead of UTC, shifting every date/time by the browser's UTC offset. Plain date-only
/// strings ("yyyy-MM-dd", used for e.g. dateOfBirth) are unaffected and already parse as UTC per spec.
export function parseServerDate(date: Date | string): Date {
  if (typeof date !== 'string') return date;
  const hasTimezoneDesignator = /Z$|[+-]\d{2}:\d{2}$/.test(date);
  return new Date(hasTimezoneDesignator || !date.includes('T') ? date : `${date}Z`);
}

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = parseServerDate(date);
  return new Intl.DateTimeFormat(resolveLocale(), options ?? { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

export function formatDateTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = parseServerDate(date);
  return new Intl.DateTimeFormat(
    resolveLocale(),
    options ?? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
  ).format(d);
}

export function formatTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = parseServerDate(date);
  return new Intl.DateTimeFormat(resolveLocale(), options ?? { hour: '2-digit', minute: '2-digit' }).format(d);
}
